/**
 * QA Management Dashboard
 *
 * Displays question-answer pairs from the database with search and pagination capabilities.
 * Read-only view of the QA management system with backend-powered search and pagination.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { advisorApiService } from '../../services/advisor-api';
import type { QAQuestion, QAManagementResponse, QAIncludedFilter } from '../../types/qa-management';
import { QADetailModal } from './QADetailModal';
import { QACreateModal } from './QACreateModal';
import { QASwapProgramCodeModal } from './QASwapProgramCodeModal';
import { QAHistoryModal } from './QAHistoryModal';
import { QAIncludeToggle } from './QAIncludeToggle';
import { getApiErrorMessage } from '../../utils/api-error';
import {
  capitalizeSchool,
  getProgramSchoolClass,
  getVisaTagClass,
  getStatusTagClass,
} from '../../utils/qa-helpers';
import { useAuth } from '../../contexts/AuthContext';
import '../../styles/QAManagement.css';

const APPLY_TO_ALL = 'Apply to all';
const DEFAULT_ITEMS_PER_PAGE = 5;
const ITEMS_PER_PAGE_OPTIONS = [5, 10, 25, 50, 100];
const INCLUDED_FILTER_OPTIONS: { value: QAIncludedFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'included', label: 'Included only' },
  { value: 'excluded', label: 'Excluded only' },
];

export const QAManagementDashboard: React.FC = () => {
  const { user } = useAuth();
  const [data, setData] = useState<QAManagementResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination and search state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_ITEMS_PER_PAGE);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState(''); // For debouncing
  const [pageInput, setPageInput] = useState(''); // For page number input
  const [includedFilter, setIncludedFilter] = useState<QAIncludedFilter>('all');

  // Include toggle state: the row currently being toggled (one at a time)
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [toggleError, setToggleError] = useState<string | null>(null);

  // No-match template state
  const [template, setTemplate] = useState('');
  const [templateDraft, setTemplateDraft] = useState('');
  const [templateSaving, setTemplateSaving] = useState(false);
  const [templateStatus, setTemplateStatus] = useState<'idle' | 'saved' | 'error'>('idle');

  // Modal state
  const [selectedQA, setSelectedQA] = useState<QAQuestion | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSwapCodeModal, setShowSwapCodeModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  // Fetch data from backend with pagination and search
  const fetchData = useCallback(
    async (
      page: number,
      searchQuery: string,
      pageSize: number,
      included: QAIncludedFilter = 'all'
    ) => {
      try {
        setIsLoading(true);
        setError(null);

        const offset = (page - 1) * pageSize;
        const response = await advisorApiService.getQAManagementData(
          pageSize,
          offset,
          searchQuery,
          included
        );

        if (response.data) {
          setData(response.data);
        } else {
          setError(response.error || 'Failed to load QA management data');
        }
      } catch (err) {
        console.error('Error fetching QA data:', err);
        setError(getApiErrorMessage(err, 'An error occurred'));
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  // Initial fetch on mount and handle deep linking
  useEffect(() => {
    // Check for question ID in URL query parameter
    const urlParams = new URLSearchParams(window.location.search);
    const questionId = urlParams.get('questionId');

    if (questionId) {
      // Fetch the specific question and open the modal
      const fetchQuestion = async () => {
        try {
          const response = await advisorApiService.getQAQuestion(questionId);
          if (response.data) {
            setSelectedQA(response.data);
          } else {
            console.error('Failed to load question from deep link:', response.error);
          }
        } catch (err) {
          console.error('Error fetching question from deep link:', err);
        }
      };
      fetchQuestion();
    }

    fetchData(1, '', DEFAULT_ITEMS_PER_PAGE);

    // Load no-match template
    advisorApiService.getNoMatchTemplate().then((res) => {
      if (res.data) {
        setTemplate(res.data.value);
        setTemplateDraft(res.data.value);
      }
    });
  }, [fetchData]);

  // Real-time search with debouncing
  useEffect(() => {
    // Debounce timer: wait 400ms after user stops typing
    const debounceTimer = setTimeout(() => {
      // Only fetch if search actually changed
      if (searchInput !== search) {
        setSearch(searchInput);
        setCurrentPage(1); // Reset to first page on new search
        fetchData(1, searchInput, itemsPerPage, includedFilter);
      }
    }, 400);

    // Cleanup: cancel the timer if user types again before 400ms
    return () => clearTimeout(debounceTimer);
  }, [searchInput, search, fetchData, itemsPerPage, includedFilter]);

  // Handle search on Enter key (optional - immediate search)
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      // Only fetch if search actually changed
      if (searchInput !== search) {
        setSearch(searchInput);
        setCurrentPage(1); // Reset to first page on new search
        fetchData(1, searchInput, itemsPerPage, includedFilter);
      }
    }
  };

  // Handle search clear
  const handleSearchClear = () => {
    setSearchInput('');
    // The useEffect will handle updating the search automatically
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    setPageInput(''); // Clear page input when navigating
    fetchData(newPage, search, itemsPerPage, includedFilter);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle page-size change: reset to first page and refetch with the new size
  const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSize = parseInt(e.target.value, 10);
    setItemsPerPage(newSize);
    setCurrentPage(1);
    setPageInput('');
    fetchData(1, search, newSize, includedFilter);
  };

  // Handle include-filter change: reset to first page and refetch
  const handleIncludedFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value as QAIncludedFilter;
    setIncludedFilter(value);
    setCurrentPage(1);
    setPageInput('');
    fetchData(1, search, itemsPerPage, value);
  };

  // Apply an updated row (from the table toggle or the detail modal) without a
  // refetch. Under an inclusion filter the row no longer belongs on the page,
  // so it is removed and the totals adjusted; under 'all' it is patched in place.
  const applyUpdatedQuestion = useCallback(
    (updated: QAQuestion) => {
      setSelectedQA((prev) => (prev?.questionId === updated.questionId ? updated : prev));
      setData((prev) => {
        if (!prev) return prev;
        const existing = prev.questions.find((q) => q.questionId === updated.questionId);
        if (!existing) return prev;
        const inclusionChanged = existing.isIncluded !== updated.isIncluded;
        const excludedDelta = !inclusionChanged ? 0 : updated.isIncluded ? -1 : 1;
        const dropFromPage = inclusionChanged && includedFilter !== 'all';
        return {
          ...prev,
          questions: dropFromPage
            ? prev.questions.filter((q) => q.questionId !== updated.questionId)
            : prev.questions.map((q) => (q.questionId === updated.questionId ? updated : q)),
          statistics: {
            ...prev.statistics,
            excludedRecords: prev.statistics.excludedRecords + excludedDelta,
          },
          pagination: {
            ...prev.pagination,
            total: prev.pagination.total - (dropFromPage ? 1 : 0),
          },
        };
      });
    },
    [includedFilter]
  );

  // Include / exclude a Q&A from chatbot retrieval from the table row.
  const handleToggleIncluded = async (qa: QAQuestion, nextIncluded: boolean) => {
    if (qa.isIncluded === nextIncluded || togglingId) return;

    setTogglingId(qa.questionId);
    setToggleError(null);

    try {
      const response = await advisorApiService.setQAIncluded(qa.questionId, nextIncluded);

      if (response.data) {
        applyUpdatedQuestion(response.data);
      } else {
        setToggleError(response.error || 'Failed to update inclusion');
      }
    } catch (err) {
      console.error('Failed to toggle inclusion:', err);
      setToggleError(getApiErrorMessage(err, 'Failed to update inclusion'));
    } finally {
      setTogglingId(null);
    }
  };

  // Handle page number input
  const handlePageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Only allow numbers
    if (value === '' || /^\d+$/.test(value)) {
      setPageInput(value);
    }
  };

  // Handle go to page on Enter or button click
  const handleGoToPage = () => {
    const pageNumber = parseInt(pageInput, 10);
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      handlePageChange(pageNumber);
    }
  };

  // Handle Enter key for page input
  const handlePageInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleGoToPage();
    }
  };

  const handleSaveTemplate = async () => {
    setTemplateSaving(true);
    setTemplateStatus('idle');
    try {
      const res = await advisorApiService.updateNoMatchTemplate(templateDraft);
      if (res.data) {
        setTemplate(res.data.value);
        setTemplateDraft(res.data.value);
        setTemplateStatus('saved');
        setTimeout(() => setTemplateStatus('idle'), 3000);
      } else {
        setTemplateStatus('error');
      }
    } catch {
      setTemplateStatus('error');
    } finally {
      setTemplateSaving(false);
    }
  };

  // Check if user is admin
  const isAdmin = user?.role === 'admin';
  const canEdit = isAdmin || user?.role === 'user';

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  // Calculate pagination info
  const totalPages = data ? Math.ceil(data.pagination.total / itemsPerPage) : 0;
  const hasNextPage = data?.pagination.hasMore || false;
  const hasPrevPage = currentPage > 1;

  if (isLoading && !data) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading QA management data...</p>
      </div>
    );
  }

  if (error) {
    // Check if this is the "no questions found" error (404)
    const isNoQuestionsError = error.toLowerCase().includes('no questions found');

    if (isNoQuestionsError) {
      return (
        <div style={{ padding: '2rem' }}>
          <h1>Question Answer Management</h1>
          <div
            style={{
              marginTop: '2rem',
              padding: '2rem',
              background: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: '4px',
              textAlign: 'center',
            }}
          >
            <h2 style={{ color: '#856404', marginBottom: '1rem' }}>No Questions Available</h2>
            <p style={{ color: '#856404', fontSize: '16px' }}>
              The question answer database is empty. Please ask your administrator to load the
              questions and answers into the database first.
            </p>
          </div>
        </div>
      );
    }

    // For other errors, show generic error message
    return (
      <div className="error-box-wrap">
        <div className="error-box">
          <h3>Error</h3>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div style={{ padding: '2rem' }}>
        <p>No data available</p>
      </div>
    );
  }

  const questions = data.questions;

  return (
    <div className="qa-dashboard">
      <div className="qa-header">
        <h1>Question Answer Management</h1>
        <button
          type="button"
          className="qa-history-button"
          onClick={() => setShowHistoryModal(true)}
        >
          History
        </button>
      </div>

      {/* Search Bar */}
      <div className="qa-control-card">
        <div className="qa-control-left full-width">
          <input
            type="text"
            placeholder="Search question or answer..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            className="qa-search-input wide"
          />
          {searchInput.trim() !== '' && (
            <button
              type="button"
              className="search-clear-button"
              onClick={handleSearchClear}
              aria-label="Clear search"
            >
              ✕
            </button>
          )}
        </div>
        <label className="qa-include-filter">
          <span className="qa-include-filter-label">Include:</span>
          <select
            className="qa-include-filter-select"
            value={includedFilter}
            onChange={handleIncludedFilterChange}
            disabled={isLoading}
            aria-label="Filter by inclusion"
          >
            {INCLUDED_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {/* Overview Section */}
      <div className="qa-overview-section">
        <h2>Overview</h2>
        <div className="qa-overview-cards">
          <div className="qa-overview-card">
            <div className="qa-overview-label">Total Q&A Pairs in Database</div>
            <div className="qa-overview-value">{data.statistics.totalRecords.toLocaleString()}</div>
            <div className="qa-overview-desc">All unique question-answer pairs</div>
          </div>
          <div className="qa-overview-card">
            <div className="qa-overview-label">Excluded from Chatbot</div>
            <div className="qa-overview-value">
              {data.statistics.excludedRecords.toLocaleString()}
            </div>
            <div className="qa-overview-desc">Kept in database, not used for answers</div>
          </div>
          <div className="qa-overview-card">
            <div className="qa-overview-label">
              {search ? 'Matching Results' : 'Showing on This Page'}
            </div>
            <div className="qa-overview-value">
              {search ? data.pagination.total.toLocaleString() : questions.length}
            </div>
            <div className="qa-overview-desc">
              {search ? 'Questions matching your search' : `Page ${currentPage} of ${totalPages}`}
            </div>
          </div>
        </div>

        {/* Default Answer Template */}
        <div className="qa-overview-card qa-template-card">
          <div className="qa-template-header">
            <div className="qa-overview-label">Default Answer Template</div>
            <div className="qa-template-actions">
              {templateStatus === 'saved' && (
                <span className="qa-template-status saved">Saved</span>
              )}
              {templateStatus === 'error' && (
                <span className="qa-template-status error">Failed to save</span>
              )}
              <button
                type="button"
                className="qa-template-save-btn"
                onClick={handleSaveTemplate}
                disabled={templateSaving || templateDraft === template}
              >
                {templateSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
          <textarea
            className="qa-template-textarea"
            value={templateDraft}
            onChange={(e) => setTemplateDraft(e.target.value)}
            rows={5}
            placeholder="Enter the message shown when no matching answer is found…"
          />
          <div className="qa-overview-desc">Shown to students when no FAQ match is found</div>
        </div>
      </div>

      {/* Table Header */}
      <div className="qa-table-header">
        <div className="qa-table-title-row">
          <h2>Question Answers</h2>
          <button
            type="button"
            className="qa-create-button"
            onClick={() => setShowCreateModal(true)}
            disabled={!isAdmin && user?.role !== 'user'}
            title={
              !isAdmin && user?.role !== 'user'
                ? `You need 'user' or 'admin' permission to create. Current role: ${user?.role || 'none'}`
                : 'Create a new question-answer artifact'
            }
          >
            Create
          </button>
          <button
            type="button"
            className="qa-swap-code-button"
            onClick={() => setShowSwapCodeModal(true)}
            disabled={!isAdmin && user?.role !== 'user'}
            title={
              !isAdmin && user?.role !== 'user'
                ? `You need 'user' or 'admin' permission to update. Current role: ${user?.role || 'none'}`
                : 'Replace a program code across all associated Q&As'
            }
          >
            Update Program Code
          </button>
        </div>
        <div className="qa-table-stats">
          <span className="qa-table-stat-item">
            Showing {questions.length} of {data.pagination.total.toLocaleString()}
            {search && ' matching results'}
          </span>
          <label className="qa-page-size">
            <span className="qa-page-size-label">Per page:</span>
            <select
              className="qa-page-size-select"
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              disabled={isLoading}
            >
              {ITEMS_PER_PAGE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {toggleError && (
        <div className="qa-toggle-error" role="alert">
          {toggleError}
        </div>
      )}

      {/* Results Table */}
      {questions.length === 0 ? (
        <div className="qa-empty">
          {search
            ? 'No questions match your search. Try different keywords.'
            : includedFilter === 'excluded'
              ? 'No Q&As are excluded. Set Include to "No" on a row to exclude it.'
              : 'No questions available.'}
        </div>
      ) : (
        <>
          <div className="qa-table-wrapper">
            <table className="qa-table">
              <thead>
                <tr>
                  <th style={{ width: '17%' }}>Question</th>
                  <th style={{ width: '20%' }}>Answer Preview</th>
                  <th style={{ width: '19%' }}>Program Codes</th>
                  <th style={{ width: '15%' }}>Visa Requirements</th>
                  <th style={{ width: '7%' }}>Status</th>
                  <th
                    style={{ width: '9%' }}
                    title="Yes = used by the chatbot. No = kept in the database but never retrieved."
                  >
                    Include
                  </th>
                  <th style={{ width: '13%' }}>Updated</th>
                </tr>
              </thead>
              <tbody>
                {questions.map((qa) => {
                  const answerPreview =
                    qa.answer.length > 220 ? `${qa.answer.slice(0, 220)}…` : qa.answer;

                  return (
                    <tr
                      key={qa.questionId}
                      className={`qa-table-row${qa.isIncluded ? '' : ' excluded'}`}
                      onClick={() => {
                        setSelectedQA(qa);
                        // Update URL with question ID for deep linking
                        const url = new URL(window.location.href);
                        url.searchParams.set('questionId', qa.questionId);
                        window.history.pushState({}, '', url.toString());
                      }}
                    >
                      <td>
                        <div className="qa-question">{qa.question || 'Untitled question'}</div>
                      </td>
                      <td>
                        <div className="qa-answer-preview">{answerPreview}</div>
                      </td>
                      <td>
                        <div className="program-code-list">
                          {[...qa.programCodes]
                            .sort((a, b) => a.localeCompare(b))
                            .map((code, idx) => {
                              const info = qa.programInfo.find((p) => p.programCode === code);
                              const tooltipText =
                                info?.studentType && info?.school && info?.program
                                  ? `${info.studentType} | ${capitalizeSchool(info.school)} | ${info.program}`
                                  : '';

                              return (
                                <span
                                  key={`${qa.questionId}-prog-${idx}`}
                                  className={`qa-pill program-pill ${code === APPLY_TO_ALL ? 'school-apply-to-all' : getProgramSchoolClass(info?.school)}`}
                                  title={tooltipText}
                                >
                                  {code.toUpperCase()}
                                </span>
                              );
                            })}
                        </div>
                      </td>
                      <td>
                        <div className="visa-list">
                          {qa.visaRequirements.map((visa, idx) => (
                            <span
                              key={`${qa.questionId}-visa-${idx}`}
                              className={`qa-pill visa-pill ${getVisaTagClass(visa)}`}
                            >
                              {visa}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td>
                        <div className="status-list">
                          {qa.statuses.map((status, idx) => (
                            <span
                              key={`${qa.questionId}-status-${idx}`}
                              className={`qa-pill status-pill ${getStatusTagClass(status)}`}
                            >
                              {status}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <QAIncludeToggle
                          value={qa.isIncluded}
                          onChange={(next) => handleToggleIncluded(qa, next)}
                          disabled={!canEdit || togglingId === qa.questionId}
                          label={`Include ${qa.question}`}
                          yesTitle={
                            !canEdit
                              ? `You need 'user' or 'admin' permission to change this. Current role: ${user?.role || 'none'}`
                              : 'Include this Q&A in chatbot answers'
                          }
                          noTitle={
                            !canEdit
                              ? `You need 'user' or 'admin' permission to change this. Current role: ${user?.role || 'none'}`
                              : 'Exclude this Q&A from chatbot answers (kept in database)'
                          }
                        />
                      </td>
                      <td>
                        <div className="qa-updated-date">{formatDate(qa.updatedAt)}</div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="qa-pagination">
              <button
                type="button"
                className="qa-pagination-button"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={!hasPrevPage || isLoading}
              >
                ← Previous
              </button>

              <span className="qa-pagination-info">
                Page {currentPage} of {totalPages}
              </span>

              <button
                type="button"
                className="qa-pagination-button"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={!hasNextPage || isLoading}
              >
                Next →
              </button>

              <div className="qa-pagination-goto">
                <span className="qa-pagination-goto-label">Go to page:</span>
                <input
                  type="text"
                  className="qa-pagination-goto-input"
                  placeholder="#"
                  value={pageInput}
                  onChange={handlePageInputChange}
                  onKeyDown={handlePageInputKeyDown}
                  disabled={isLoading}
                />
                <button
                  type="button"
                  className="qa-pagination-goto-button"
                  onClick={handleGoToPage}
                  disabled={
                    isLoading ||
                    !pageInput ||
                    parseInt(pageInput, 10) < 1 ||
                    parseInt(pageInput, 10) > totalPages
                  }
                >
                  Go
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Detail Modal */}
      <QADetailModal
        qaData={selectedQA}
        onClose={() => {
          setSelectedQA(null);
          // Remove question ID from URL when closing modal
          const url = new URL(window.location.href);
          url.searchParams.delete('questionId');
          window.history.pushState({}, '', url.toString());
        }}
        onUpdate={(updated) => {
          if (updated) {
            // Toggle from the modal: patch in place like the table toggle does
            applyUpdatedQuestion(updated);
          } else {
            // Edit/delete: refresh the current page data
            fetchData(currentPage, search, itemsPerPage, includedFilter);
          }
        }}
      />

      {/* Create Modal */}
      <QACreateModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreate={() => {
          // Refresh the current page data after creation
          fetchData(currentPage, search, itemsPerPage, includedFilter);
        }}
      />

      {/* Swap Program Code Modal */}
      <QASwapProgramCodeModal
        isOpen={showSwapCodeModal}
        onClose={() => setShowSwapCodeModal(false)}
        onSwapped={() => {
          // Refresh the current page data after the code swap
          fetchData(currentPage, search, itemsPerPage, includedFilter);
        }}
      />

      {/* History Modal */}
      <QAHistoryModal isOpen={showHistoryModal} onClose={() => setShowHistoryModal(false)} />
    </div>
  );
};
